import {
  useRef,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from "react";

function classes(base: string[], className: string | undefined): string {
  if (className) {
    base.push(className);
  }

  return base.join(" ");
}

function tabElementId(tabId: string): string {
  return `marker-tab-${tabId}`;
}

function tabPanelElementId(tabId: string): string {
  return `marker-tabpanel-${tabId}`;
}

export interface TabsTabDescriptor {
  count?: number;
  id: string;
  label: string;
}

export interface TabsProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "onSelect"
> {
  activeId: string;
  ariaLabel: string;
  /** Disabled-empty loading state (e.g. transcript-timeline.md): the tab
   *  labels render but cannot be selected while the owning record loads. */
  disabled?: boolean;
  onSelect: (id: string) => void;
  tabs: Array<TabsTabDescriptor>;
}

/**
 * Horizontal facet switcher for the Dossier archetype (UI-LAW §4c): one
 * hairline row of text-only tab buttons; the active tab carries the
 * green 2px underline (§2.4 accent use) and on-surface text. Controlled
 * only — callers bind `activeId` to a `?tab=` search param because URL is UI
 * state (§4c). Activation is manual (Enter/Space/click, never focus
 * movement) so expensive panels do not mount while arrowing. Tabs never
 * nest inside Popover or Modal layers (§4a hard limits).
 */
export function Tabs({
  activeId,
  ariaLabel,
  className,
  disabled = false,
  onSelect,
  tabs,
  ...props
}: TabsProps) {
  const tabButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    focusedIndex: number,
  ) => {
    if (tabs.length === 0) {
      return;
    }

    let nextFocusIndex: number;
    switch (event.key) {
      case "ArrowRight":
        nextFocusIndex = (focusedIndex + 1) % tabs.length;
        break;
      case "ArrowLeft":
        nextFocusIndex = (focusedIndex - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        nextFocusIndex = 0;
        break;
      case "End":
        nextFocusIndex = tabs.length - 1;
        break;
      case "Enter":
      case " ": {
        // Manual activation: selection happens only here and on click —
        // arrow keys move focus without selecting, so panels that are
        // expensive to mount are not churned while navigating.
        event.preventDefault();
        const focusedTab = tabs[focusedIndex];
        if (focusedTab) {
          onSelect(focusedTab.id);
        }
        return;
      }
      default:
        return;
    }

    event.preventDefault();
    tabButtonRefs.current[nextFocusIndex]?.focus();
  };

  return (
    <div
      aria-label={ariaLabel}
      className={classes(["marker-tabs"], className)}
      role="tablist"
      {...props}
    >
      {tabs.map((tab, index) => {
        const isActive = tab.id === activeId;
        const tabClasses = ["marker-tabs__tab"];
        if (isActive) {
          tabClasses.push("marker-tabs__tab--active");
        }

        return (
          <button
            key={tab.id}
            ref={(element) => {
              tabButtonRefs.current[index] = element;
            }}
            aria-controls={tabPanelElementId(tab.id)}
            aria-selected={isActive}
            className={tabClasses.join(" ")}
            disabled={disabled}
            id={tabElementId(tab.id)}
            onClick={() => {
              onSelect(tab.id);
            }}
            onKeyDown={(event) => {
              handleTabKeyDown(event, index);
            }}
            role="tab"
            tabIndex={isActive ? 0 : -1}
            type="button"
          >
            {tab.label}
            {tab.count !== undefined ? (
              <span className="marker-tabs__count">{tab.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  activeId: string;
  children: ReactNode;
  /** §4b.3: set true when the panel hosts a fill-mode region (fill-mode
   *  DataTable/PaginatedTableSection, SplitPane) inside a
   *  `Page variant="workspace"` — the panel then passes the flex-height
   *  chain through (flex column, flex: 1, min-height: 0) so the region owns
   *  the remaining height and its own scroll. Leave unset for panels in
   *  scrolling config/dossier pages, which stay plain blocks. */
  fill?: boolean;
  tabId: string;
}

/**
 * The content surface for one Tabs facet (UI-LAW §4c Dossier archetype).
 * Inactive panels render null — unmounted, not hidden — so facet content
 * (tables, charts, traces) exists only while its tab is selected, matching
 * the manual-activation contract on Tabs.
 */
export function TabPanel({
  activeId,
  children,
  className,
  fill = false,
  tabId,
  ...props
}: TabPanelProps) {
  if (tabId !== activeId) {
    return null;
  }

  const baseClasses = ["marker-tabs__panel"];
  if (fill) {
    baseClasses.push("marker-tabs__panel--fill");
  }

  return (
    <div
      aria-labelledby={tabElementId(tabId)}
      className={classes(baseClasses, className)}
      id={tabPanelElementId(tabId)}
      role="tabpanel"
      tabIndex={0}
      {...props}
    >
      {children}
    </div>
  );
}
